import type {
  BasicReview,
  FetchReviewsFiltersType,
} from '@shared/domain/review';
import type { Company, User } from '../../../authentication/types/types.auth';
import { Alert, Button } from '@mui/material';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import { Page } from '@shared/components/Page';
import CustomSnackbar from '@shared/components/Snackbar';
import { UserContext } from '@/shared/contexts/UserContext';
import { useResponsive } from '@shared/hooks/useResponsive';
import { useApi } from '@shared/services/api';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import {
  OnboardingAction,
  storeOnboardingIntent,
} from '@shared/utils/onboardingIntent';
import AdminEditCompany from '@shared/admin/components/AdminEditCompany';
import SignUpOrLogInModal from '../../../authentication/components/SignUpOrLogInModal';
import CreateIntelligenceRequestModal from '../../../review-reader/components/CreateIntelligenceRequestModal';
import ReviewUnlockModal from '../../../review/components/DeepDiveReviewCountModal';
import ReviewDialog from '../../../review/components/ReviewDialog';
import { useReviewContext } from '../../../review/context/ReviewContext';
import CompanyProfileEmptyState from './CompanyProfileEmptyState';
import CompanyProfileHeader from './CompanyProfileHeader';
import CompanyProfileReviews from './CompanyProfileReviews';
import ThreadsPanel from './threads/ThreadsPanel';
import TrialExpiredPaywall from './TrialExpiredPaywall';

function CompanyProfile() {
  const [currentCompany, setCurrentCompany] = useState<Company>();
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [openReviewUnlockModal, setOpenReviewUnlockModal] = useState(false);
  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<number>();
  const [pendingReview, setPendingReview] = useState<BasicReview | null>(null);
  const [openAdminEditModal, setOpenAdminEditModal] = useState(false);
  const [currentReviewIds, setCurrentReviewIds] = useState<number[]>([]);
  const [currentFilters, setCurrentFilters] = useState<FetchReviewsFiltersType>(
    {
      dealType: [],
      dealArea: [],
      dealWon: '',
      dealDate: '',
      dealSize: [0, 6], // Default range
    }
  );
  const latestReviewIdsRef = useRef<number[]>([]);

  const { user, fetchUsageInfo, refetchUser } = useContext(UserContext);
  const api = useApi();
  const { openMessage } = useMessageService();

  const { name } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const planMaxed = searchParams.get('planMaxed');
  const openReviewModal = searchParams.get('openReviewModal') === 'true';
  const scrollToThreadId = searchParams.get('scrollTo');
  const highlightThreadId = searchParams.get('highlight');
  const subscriptionRefresh =
    searchParams.get('subscriptionRefresh') === 'true';

  const isTrialExpired =
    !user?.isAdmin && user?.subscriptionStatus === 'trial_expired';

  const isCompanyFollowed = user?.followedCompanies?.find(
    (company) => company?.id === currentCompany?.id
  );

  const { snackbarConfig, setSnackbarConfig, setReviewId } = useReviewContext();
  const { isMobile } = useResponsive();

  const totalReviewCount = useRef<number | null>(null);

  // Sticky header state
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const headerPlaceholderRef = useRef<HTMLDivElement>(null);
  const [originalHeaderPosition, setOriginalHeaderPosition] =
    useState<number>(0);

  const reviewDialogChange = () => {
    setOpenReviewDialog(true);
  };

  const handleModalProceed = () => {
    navigate(
      `/review-reader/${encodeURIComponent(name!)}/${selectedReviewId}?unlock=true`
    );
  };

  const handleSnackBarClose = () => {
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  // Wrapper to track when reviews have been loaded
  const handleReviewsLoaded = useCallback((reviewIds: number[]) => {
    setCurrentReviewIds(reviewIds);
  }, []);

  const getCompanyInfo = useCallback(async () => {
    try {
      const res = await api.get(`company/name/${encodeURIComponent(name!)}`);
      if (res.error) {
        throw new Error(res.message);
      }
      setCurrentCompany(res.data);
    } catch {
      setSnackbarConfig({
        open: true,
        type: 'error',
        message: 'Server error, please try again later',
      });
    }
  }, [api, name]);

  const followCompany = async (newlyAuthenticatedUser?: User) => {
    const effectiveUser = newlyAuthenticatedUser ?? user;

    if (!effectiveUser) {
      setSnackbarConfig({
        message: 'You must be logged in to use this feature.',
        open: true,
        type: 'error',
      });
      return;
    }

    const updateCompanyInput = {
      companyId: currentCompany?.id,
      userId: effectiveUser.id,
    };

    await api.post(
      isCompanyFollowed ? `users/unfollow-company` : `users/follow-company`,
      updateCompanyInput
    );
    refetchUser();
  };

  const handleOpenReviewDialog = (fetchReviewId?: number) => {
    if (!user) {
      // Store intent to create review after onboarding
      storeOnboardingIntent({
        action: OnboardingAction.CreateReview,
        returnUrl: window.location.pathname,
        companyId: String(currentCompany?.id),
        companyName: currentCompany?.companyName,
      });
      setOpenAuthModal(true);
      return;
    }
    setReviewId(fetchReviewId || 0);
    setOpenReviewDialog(true);
  };

  const handleViewDealReviewClick = (
    review: BasicReview,
    newlyAuthenticatedUser?: User
  ) => {
    const effectiveUser = newlyAuthenticatedUser ?? user;
    if (!effectiveUser) {
      storeOnboardingIntent({
        action: OnboardingAction.ViewDeepDive,
        returnUrl: window.location.pathname,
        companyId: String(currentCompany?.id),
        companyName: currentCompany?.companyName,
        reviewId: String(review.id),
      });
      setPendingReview(review);
      setOpenAuthModal(true);
      return;
    }

    navigate(`/review-reader/${encodeURIComponent(name!)}/${review.id}`);
  };

  const handleAuthSuccess = (user: User) => {
    setOpenAuthModal(false);
    if (pendingReview) {
      handleViewDealReviewClick(pendingReview, user);
      setPendingReview(null);
    }
  };

  const handleCreateIntelligenceRequest = () => {
    if (!user) {
      const intent = {
        action: OnboardingAction.CreateIntelligenceRequest,
        returnUrl: window.location.pathname,
        companyId: String(currentCompany?.id),
        companyName: currentCompany?.companyName,
      };
      storeOnboardingIntent(intent);
      setOpenAuthModal(true);
      return;
    }
    openMessage({
      modalType: 'action',
      header: 'Request Consultation',
      customBody: (
        <CreateIntelligenceRequestModal companyId={currentCompany?.id} />
      ),
      size: 'large',
    });
  };

  const handleSaveAndApproveCompany = async (
    updatedCompany: Partial<Company>
  ) => {
    try {
      // First update the company data
      const updateRes = await api.put(`admin/company/${currentCompany?.id}`, {
        ...updatedCompany,
        isApproved: true,
      });
      if (updateRes.error) {
        throw new Error(updateRes.message);
      }

      setCurrentCompany({
        ...currentCompany!,
        ...updatedCompany,
        isApproved: true,
      } as Company);
      setOpenAdminEditModal(false);
      setSnackbarConfig({
        open: true,
        type: 'success',
        message: 'Company updated and approved successfully',
      });
    } catch {
      setSnackbarConfig({
        open: true,
        type: 'error',
        message: 'Error updating company, please try again later',
      });
    }
  };

  useEffect(() => {
    // Reset company-specific state when navigating to a new company
    setCurrentCompany(undefined);
    setSelectedReviewId(undefined);
    setPendingReview(null);
    setOpenReviewDialog(false);
    setOpenReviewUnlockModal(false);
    setOpenAuthModal(false);

    getCompanyInfo();
  }, [name]);

  useEffect(() => {
    if (currentCompany?.id && user?.idToken) {
      fetchUsageInfo();
    }
  }, [currentCompany?.id, user?.idToken]);

  useEffect(() => {
    if (planMaxed) {
      setOpenReviewUnlockModal(true);
    }
  }, [planMaxed]);

  // When returning from billing portal, sync subscription and refetch user data
  useEffect(() => {
    if (!subscriptionRefresh || !user?.idToken) return;
    // Remove the param from the URL so it doesn't re-trigger on back/forward
    const next = new URLSearchParams(location.search);
    next.delete('subscriptionRefresh');
    navigate({ search: next.toString() }, { replace: true });
    // Sync from Stripe and refresh user context
    api.get('subscription/sync').then(() => refetchUser());
  }, [subscriptionRefresh, user?.idToken]);

  useEffect(() => {
    setReviewId(null);
  }, []);

  // Sync latestReviewIdsRef to state after ReviewFilters renders
  // Use layoutEffect to sync before browser paint, ensuring reviewIds are available
  // for the auto-open effect that runs after this
  useLayoutEffect(() => {
    const reviewIdsJson = JSON.stringify(latestReviewIdsRef.current);
    const currentJson = JSON.stringify(currentReviewIds);
    if (
      reviewIdsJson !== currentJson &&
      latestReviewIdsRef.current.length > 0
    ) {
      setCurrentReviewIds(latestReviewIdsRef.current);
    }
  });

  useEffect(() => {
    const completedAction = sessionStorage.getItem('completedOnboardingAction');

    if (completedAction && currentCompany?.id) {
      try {
        const action = JSON.parse(completedAction);

        if (
          action.type === 'followCompany' &&
          Number(action.companyId) === currentCompany.id
        ) {
          setTimeout(() => {
            handleCreateIntelligenceRequest();
          }, 500);
          sessionStorage.removeItem('completedOnboardingAction');
        } else if (
          action.type === 'createIntelligenceRequest' &&
          Number(action.companyId) === currentCompany.id
        ) {
          setTimeout(() => {
            handleCreateIntelligenceRequest();
          }, 500);
          sessionStorage.removeItem('completedOnboardingAction');
        }
      } catch {
        sessionStorage.removeItem('completedOnboardingAction');
      }
    }
  }, [currentCompany?.id]);

  useEffect(() => {
    if (!currentCompany) return;

    totalReviewCount.current = currentCompany?.reviewCount || 0;
  }, [currentCompany]);

  useEffect(() => {
    if (!user?.idToken) return;
    refetchUser();
  }, [user?.idToken]);

  useEffect(() => {
    if (openReviewModal) {
      setOpenReviewDialog(true);
    }
  }, [openReviewModal]);


  // Scroll detection for sticky header
  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) {
        return;
      }

      // Store original position on first render
      if (originalHeaderPosition === 0) {
        const rect = headerRef.current.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        setOriginalHeaderPosition(rect.top + scrollTop);
        return;
      }

      const scrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      const shouldBeSticky =
        !isMobile && scrollPosition > originalHeaderPosition;

      if (shouldBeSticky !== isHeaderSticky) {
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    // Set initial position
    handleScroll();

    // Add scroll listener
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [originalHeaderPosition, isHeaderSticky, isMobile]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    const companyProfileHeaderHeight = headerRef.current?.offsetHeight || 0;
    if (element) {
      const offsetTop =
        element.offsetTop - (isMobile ? 0 : companyProfileHeaderHeight + 5); // Adjust for sticky header
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  // Handle URL anchor on initial load
  useEffect(() => {
    if (currentCompany && location.hash) {
      const sectionId = location.hash.slice(1); // Remove the # prefix
      // Small delay to ensure DOM elements are rendered
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
    }
  }, [currentCompany, location.hash]);

  useEffect(() => {
    if (!currentCompany || !scrollToThreadId) {
      return;
    }

    const initialDelay = 2000;

    const scrollToElement = () => {
      const element = document.getElementById(scrollToThreadId);

      if (element) {
        const rect = element.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const headerHeight = headerRef.current?.offsetHeight || 0;
        const targetPosition =
          scrollTop + rect.top - (isMobile ? 20 : headerHeight + 40);

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });

        setTimeout(() => {
          element.classList.add('thread-attention');
          setTimeout(() => {
            element.classList.remove('thread-attention');
          }, 2000);
        }, 500);
      }
    };

    const timeoutId = setTimeout(scrollToElement, initialDelay);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [currentCompany, scrollToThreadId, isMobile]);

  if (!currentCompany) {
    return (
      <LoadingSpinner
        variant="pulse"
        size="large"
        message={`Loading ${name}...`}
        fullScreen
        className="loading-spinner--network-blue"
      />
    );
  }

  const seoTitle = `${name} Company Profile`;
  const seoDescription = `Research ${name} and access sales insights, deal retros, and intelligence from other sales professionals. Get detailed information about selling to ${name} including insights into procurement, evaluation process, legal review, security review, RFPs, economic buyers, and other deal-closing intel.`;

  return (
    <Page
      title={seoTitle}
      subtitle={seoDescription}
      description={seoDescription}
      embedded={true}
    >
      <div className="company-profile-page">
      <ReviewDialog
        open={openReviewDialog}
        setOpen={setOpenReviewDialog}
        companyName={currentCompany.companyName || ''}
        companyId={currentCompany.id || 0}
      />
      {!currentCompany.isApproved && (
        <Alert
          severity="warning"
          variant="filled"
          sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
        >
          This company is pending approval
          {user?.isAdmin && (
            <Button
              variant="contained"
              size="small"
              sx={{ ml: 2 }}
              onClick={() => setOpenAdminEditModal(true)}
            >
              Review & Approve
            </Button>
          )}
        </Alert>
      )}
      {user?.isAdmin && (
        <AdminEditCompany
          isOpen={openAdminEditModal}
          setIsOpen={() => setOpenAdminEditModal(false)}
          company={currentCompany}
          onSave={handleSaveAndApproveCompany}
        />
      )}
      {/* Placeholder to prevent jumping */}
      <div
        ref={headerPlaceholderRef}
        style={{
          height: isHeaderSticky && !isMobile ? '90px' : '0px',
          transition: 'height 0.2s ease',
        }}
      />
      <CompanyProfileHeader
        ref={headerRef}
        company={currentCompany}
        openReviewDialog={reviewDialogChange}
        followCompany={followCompany}
        totalReviews={totalReviewCount.current || 0}
        isSticky={isHeaderSticky && !isMobile}
        onSectionClick={scrollToSection}
        hasReviews={(currentCompany.reviewCount || 0) > 0}
        reviewIds={currentReviewIds}
        hasWorkspace={false}
        isTrialExpired={isTrialExpired}
      />
      <>
        {currentCompany.reviewCount === 0 ? (
          user?.isAdmin ? (
            <div id="filters">
              <div id="company-profile-ratings">
                <CompanyProfileReviews
                  handleOpenReviewDialog={handleOpenReviewDialog}
                  title={`${name} Reviews`}
                  handleViewDealReviewClick={handleViewDealReviewClick}
                  companyId={currentCompany.id}
                  onReviewsLoaded={handleReviewsLoaded}
                  onFiltersChanged={setCurrentFilters}
                  onlyOwnReviews={true}
                />
              </div>
            </div>
          ) : (
            <CompanyProfileEmptyState
              companyName={currentCompany.companyName || name || ''}
              onLeaveReview={() => handleOpenReviewDialog()}
              onCreateIntelligenceRequest={handleCreateIntelligenceRequest}
              isTrialExpired={isTrialExpired}
              hideIntelligenceRequest={true}
            />
          )
        ) : (
          // We have reviews to show
          <div id="filters">
            <div id="company-profile-ratings">
              <CompanyProfileReviews
                handleOpenReviewDialog={handleOpenReviewDialog}
                title={`${name} Deal Reviews`}
                subtitle="Read detailed deal reviews from sales professionals who have sold to this company."
                handleViewDealReviewClick={handleViewDealReviewClick}
                companyId={currentCompany.id}
                onReviewsLoaded={handleReviewsLoaded}
                onFiltersChanged={setCurrentFilters}
              />
            </div>
          </div>
        )}
        {/* Content always rendered — paywall overlays on top when trial expired */}
        <div style={{ position: 'relative' }}>
          <div id="threads">
            <ThreadsPanel
              companyId={currentCompany.id || 0}
              companyName={currentCompany.companyName || name || ''}
              highlightThreadId={
                !isTrialExpired ? highlightThreadId || undefined : undefined
              }
              isTrialExpired={isTrialExpired}
            />
          </div>
          {isTrialExpired && (
            <TrialExpiredPaywall
              companyName={currentCompany.companyName || name}
              isContributor={user?.planSlug === 'contributor'}
            />
          )}
        </div>
      </>

      {openReviewUnlockModal && (
        <ReviewUnlockModal
          open={openReviewUnlockModal}
          onCancel={() => setOpenReviewUnlockModal(false)}
          maxConsumableDeepDivesInSubPeriod={
            user?.maxConsumableDeepDivesInSubPeriod ?? null
          }
          consumedDeepDives={user?.deepDivesViewedInSubPeriod || 0}
          cancelText="Back to Company Profile"
          onProceed={handleModalProceed}
        />
      )}
      <SignUpOrLogInModal
        open={openAuthModal}
        onClose={() => {
          setOpenAuthModal(false);
          setPendingReview(null);
        }}
        contextMessage={`Create an account to read detailed deal retros for ${name}`}
        handleAuthSuccess={handleAuthSuccess}
        initialMode="signup"
      />
      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
      </div>
    </Page>
  );
}

export default CompanyProfile;
